import React, { useMemo, useCallback, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GlgDisabledElementWrapper } from 'Shared/components'

import LeaguesBrowserItem from './LeaguesBrowserItem'
import LeaguesBrowserPagination from './LeaguesBrowserPagination'
import LeaguesBrowserLegend from './LeaguesBrowserLegend'
import { setSelectedLeaguesForCloning, selectSelectedLeaguesForCloning } from '../../reducers/selected_leagues_for_cloning'
import {
  selectPotentialLeaguesForCloning,
  selectPotentialLeaguesForCloningLoading,
  selectPotentialLeaguesForCloningPagination,
} from '../../reducers/potential_leagues_for_cloning'

import { MAX_ALLOWED_LEAGUES_FOR_CLONING } from '../../constants'
import { GlgLoadingOverlay } from '../../../shared/components'

const getLeagueIDsSet = (leaguesList) => {
  return new Set(leaguesList.map(league => league.id))
}

const LeaguesBrowserList = () => {
  const dispatch = useDispatch()
  const isLoading = useSelector(selectPotentialLeaguesForCloningLoading)
  const pagination = useSelector(selectPotentialLeaguesForCloningPagination)
  const pagedLeagues = useSelector(selectPotentialLeaguesForCloning)
  const selectedLeagues = useSelector(selectSelectedLeaguesForCloning)

  // Split into two columns
  const leftColumn = pagedLeagues.slice(0, 10)
  const rightColumn = pagedLeagues.slice(10, 20)

  const isWholePageSelected = useMemo(() => {
    return pagedLeagues.length && getLeagueIDsSet(pagedLeagues).isSubsetOf(getLeagueIDsSet(selectedLeagues))
  }, [ pagedLeagues, selectedLeagues ])

  const removeLeaguesFromSelected = useCallback((leaguesList) => {
    const leaguesToBeRemovedIDs = getLeagueIDsSet(leaguesList)
    const remainingLeagues = selectedLeagues.filter(league => !leaguesToBeRemovedIDs.has(league.id))
    dispatch(setSelectedLeaguesForCloning(remainingLeagues))
  }, [ dispatch, selectedLeagues ])

  const getNewSelectedLeagues = useCallback((leaguesList) => {
    const existingLeagueIDs = getLeagueIDsSet(selectedLeagues)
    const leaguesToBeAdded = leaguesList.filter(league => !existingLeagueIDs.has(league.id))

    return [ ...selectedLeagues, ...leaguesToBeAdded ]
  }, [ selectedLeagues ])


  const addLeaguesToSelected = useCallback((leaguesList) => {
    const newSelectedLeagues = getNewSelectedLeagues(leaguesList)

    dispatch(setSelectedLeaguesForCloning(newSelectedLeagues))
  }, [ dispatch, selectedLeagues ])

  const [ anchorIndex, setAnchorIndex ] = useState(null)
  const [ anchorAction, setAnchorAction ] = useState(null)

  useEffect(() => {
    setAnchorIndex(null)
    setAnchorAction(null)
  }, [ pagedLeagues ])

  const handleCheckboxChange = useCallback((league, isShift) => {
    const selectedIDs = getLeagueIDsSet(selectedLeagues)
    const currentIndex = pagedLeagues.findIndex(l => l.id === league.id)

    if (isShift && anchorIndex !== null) {
      const from = Math.min(anchorIndex, currentIndex)
      const to = Math.max(anchorIndex, currentIndex)
      const rangeLeagues = pagedLeagues.slice(from, to + 1)

      if (anchorAction === 'select') {
        const toAdd = rangeLeagues.filter(l => !selectedIDs.has(l.id)).slice(0, remainingSlots)
        dispatch(setSelectedLeaguesForCloning([ ...selectedLeagues, ...toAdd ]))
      } else {
        const toRemoveIDs = getLeagueIDsSet(rangeLeagues)
        dispatch(setSelectedLeaguesForCloning(selectedLeagues.filter(l => !toRemoveIDs.has(l.id))))
      }
    } else {
      const isCurrentlySelected = selectedIDs.has(league.id)
      if (isCurrentlySelected) {
        removeLeaguesFromSelected([ league ])
        setAnchorAction('unselect')
      } else {
        addLeaguesToSelected([ league ])
        setAnchorAction('select')
      }
      setAnchorIndex(currentIndex)
    }
  }, [ selectedLeagues, pagedLeagues, anchorIndex, anchorAction ])

  const maxLeaguesHaveBeenSelected = React.useMemo(() => {
    return selectedLeagues.length >= MAX_ALLOWED_LEAGUES_FOR_CLONING
  }, [ selectedLeagues ])

  const hasAnyPageLeagueSelected = pagedLeagues.some(league => getLeagueIDsSet(selectedLeagues).has(league.id))

  const remainingSlots = MAX_ALLOWED_LEAGUES_FOR_CLONING - selectedLeagues.length

  const handleSelectAll = useCallback(() => {
    const existingLeagueIDs = getLeagueIDsSet(selectedLeagues)
    const unselectedPageLeagues = pagedLeagues.filter(league => !existingLeagueIDs.has(league.id))
    const leaguesToAdd = unselectedPageLeagues.slice(0, remainingSlots)
    dispatch(setSelectedLeaguesForCloning([ ...selectedLeagues, ...leaguesToAdd ]))
  }, [ dispatch, selectedLeagues, pagedLeagues, remainingSlots ])


  return (
    <GlgLoadingOverlay isLoading={isLoading} >
      <div className="cls-leagues-browser-list">
        {/* Search Section */}
        <div className="cls-view-header-container">
          <div className="cls-lb-search-title">
            <span className="cls-view-panel-text">
              {window.I18n.t('clone_leagues_sessions.leagues_search.available_to_clone')}
            </span>
            <span className="cls-lb-counter-text">
              {pagination?.totalCount}
            </span>
          </div>
        </div>

        {/* Select / Deselect all button*/}
        {(isWholePageSelected || (maxLeaguesHaveBeenSelected && hasAnyPageLeagueSelected)) ?
          <div>
            <button
              onClick={() => removeLeaguesFromSelected(pagedLeagues)}
              className="cls-controls-text"
            >
              {window.I18n.t('buttons.unselect_all')}
            </button>
          </div>
            :
          <GlgDisabledElementWrapper disabled={maxLeaguesHaveBeenSelected}>
            <button
              onClick={handleSelectAll}
              className="cls-controls-text"
            >
              {window.I18n.t('buttons.select_all')}
            </button>
          </GlgDisabledElementWrapper>
        }

        {/* Two Column Layout */}
        <div className="cls-lb-columns-container">
          {[ leftColumn, rightColumn ].map((columnData, index) => (
            <div key={index} className="cls-lb-column">
              {columnData.map((league) => {
                const isElementSelected = getLeagueIDsSet(selectedLeagues).has(league.id)
                return (
                  <GlgDisabledElementWrapper
                    key={league.id}
                    disabled={maxLeaguesHaveBeenSelected && !isElementSelected}
                  >
                    <LeaguesBrowserItem
                      item={league}
                      onCheckChanged={(isShift) => handleCheckboxChange(league, isShift)}
                      isSelected={isElementSelected}
                    />
                  </GlgDisabledElementWrapper>
                )
              },
              )}
            </div>
          ))}
        </div>
        <div className="cls-lb-footer">
          <LeaguesBrowserLegend />
          <LeaguesBrowserPagination />
        </div>
      </div>
    </GlgLoadingOverlay>
  )
}

export default LeaguesBrowserList
